import { Component } from 'react'
import PropTypes from 'prop-types'
import { applyRouterMiddleware, Route, Router } from 'react-router'
// This is an older lib that predates React Hooks. Rename method to avoid
// linting errors thinking it's a proper React hook.
import { useScroll as reactRouterScroll } from 'react-router-scroll'
import { Provider } from 'react-redux'
import { clearProductFilters } from 'store/components/product-filters/actions'
import { get, pick } from 'lodash'
import qs from 'querystring'

import { api } from 'shared/api'
import { GenericErrorBoundary } from 'components/error-boundary'
import { SALE_PREVIEW_CATEGORY_SLUGS } from 'shared/constants'
import { getHistory } from 'lib/history'
import * as Actions from 'store/actions'
import { categoryTreeToCategories } from 'store/lib'

import withCurrentUser from 'shared/HOC/withCurrentUser'
import withReactQueryClientProvider from 'shared/HOC/withReactQueryClientProvider'
import { withContextualizedTracking, track } from 'lib/analytics'
import { FlagsProvider } from 'flagged'
import { displayBirthdayWishlistModal } from 'shared/BirthdayWishlistModal/BirthdayWishlistModal.utils'
import { createStore } from '../lib/redux-store'
import { catalogCategoryProductsKey } from '../api/hooks'
import {
  CatalogCategoryShape,
  CuratedProductCollectionShape,
  GuidesShape,
  HeroProductShape,
} from '../lib/prop-types'

import App from './app-container'
import { BrandPage } from './brand-page'
import { LandingPage } from './landing-page'
import { CategoryPage } from './category-page'
import SearchPage from './search-page'
import ProductDetailsWrapper from './product-details-page/product-details-wrapper'
import ContentContainer from './content-container'
import { getInitialFilterParams } from './product-filters/helpers'
import { transformCategoryTreeGuides } from './store-app/utils'

const isPDPProductChange = (prevState, nextState) => {
  // if we weren't previously on a PDP this is not a product change
  if (!get(prevState, 'params.gpId')) {
    return false
  }
  // if we have the same gpId but different productIds it's a product change
  return (
    get(prevState, 'params.gpId') === get(nextState, 'params.gpId') &&
    get(prevState, 'params.productId') !== get(nextState, 'params.productId')
  )
}

const isPathChange = (prevState, nextState) =>
  prevState.location.pathname !== nextState.location.pathname

const hydrateQueryCache = (props) => {
  const {
    queryClient,
    categorySlug,
    collections,
    curatedProductCollections,
    navbarCategoriesJson,
  } = props
  if (categorySlug && queryClient) {
    queryClient.setQueryData(
      catalogCategoryProductsKey(categorySlug, { heroes: true }),
      { heroProducts: props.hero_products }
    )
  }
  if (navbarCategoriesJson) {
    queryClient.setQueryData(['navbar_categories'], navbarCategoriesJson)
  }

  if (collections && queryClient) {
    Object.keys(collections).forEach((collectionKey) => {
      collections[collectionKey].forEach((genericProduct) => {
        if (genericProduct.activeProductId) {
          api.products.utils.primeUseProductCache(
            queryClient,
            { genericProduct },
            [genericProduct.id]
          )
        }
      })
    })
  }
  if (curatedProductCollections && curatedProductCollections.length) {
    queryClient.setQueryData(['curatedProductCollections'], {
      productCollections: curatedProductCollections,
    })
  }
}

class StoreApp extends Component {
  static handleRouteChange = (prevState, nextState) => {
    if (
      isPathChange(prevState, nextState) &&
      !isPDPProductChange(prevState, nextState)
    ) {
      window.ga('send', 'pageview', nextState.location.pathname)
    }
  }

  constructor(props) {
    super(props)

    const { isMobile } = props
    const categoryTree = transformCategoryTreeGuides(
      props.categoryTree,
      props.categoryGuides
    )

    const categories = categoryTreeToCategories(categoryTree)
    const filteredCategoryTree = categoryTree.filter(
      (category) => !SALE_PREVIEW_CATEGORY_SLUGS.includes(category.urlSlug)
    )

    this.initReduxStore(props)

    const { currentUser } = props
    if (currentUser) {
      this.dispatchCurrentUser(currentUser)
    }

    this.store.dispatch(Actions.setCategoryTree(filteredCategoryTree))
    this.store.dispatch(Actions.setCategories(categories))

    this.hydrateData(props)

    this.store.dispatch(Actions.setIsMobile(isMobile))

    this.state = { categories }
  }

  componentDidUpdate(prevProps) {
    const { currentUser } = this.props

    if (prevProps.currentUser !== currentUser) {
      this.dispatchCurrentUser(currentUser)
    }
  }

  dispatchCurrentUser = (currentUser) => {
    this.store.dispatch(
      Actions.setCashFundPreferences(
        currentUser?.currentRegistry?.cashFundPreferences
      )
    )
    this.store.dispatch(Actions.setUser(currentUser))
  }

  initialProductSortParams = (props) => {
    const queryStrMatches = props.path.match(/\?([^#]*)/)
    const hasSortParams = queryStrMatches && queryStrMatches[1]

    if (!hasSortParams) return null

    const sortParams = pick(qs.parse(queryStrMatches[1]), ['sort', 'order'])
    return sortParams
  }

  renderLandingPage = (routerProps) => {
    const { featureFlags, currentUser } = this.props

    if (currentUser && featureFlags.show_birthday_list_feature_modal) {
      displayBirthdayWishlistModal(
        { id: currentUser.registryId, ownerId: currentUser.id },
        track.EventLocation.LIST_ADOPTION_MODAL_MINUS_STORE,
        currentUser
      )
    }

    const { showAppPromoBanner, curatedProductCollections } = this.props

    return (
      <LandingPage
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...routerProps}
        productCollections={curatedProductCollections}
        showAppPromoBanner={showAppPromoBanner}
      />
    )
  }

  initReduxStore = (props) => {
    const initialState = {
      ui: {
        featureFlags: props.featureFlags,
      },
      activeSortParams: this.initialProductSortParams(props),
      productFilters: getInitialFilterParams(),
    }

    this.store = createStore(initialState)
  }

  hydrateData = (props) => {
    hydrateQueryCache(props)
  }

  clearAllProductFilters = () => this.store.dispatch(clearProductFilters())

  getRouterPropsRender = () => {
    if (typeof window === 'undefined') {
      return undefined
    }

    return applyRouterMiddleware(
      reactRouterScroll((prevRouterProps, nextRouterProps) => {
        if (prevRouterProps) {
          // do not scroll to top if replacing instead of pushing (sort, attribute change on pdp)
          return nextRouterProps.location.action !== 'REPLACE'
        }
        // do not scroll to top on first page load
        return false
      })
    )
  }

  render() {
    const { categories } = this.state

    const { path, featureFlags } = this.props

    const history = getHistory({ path, useRouter: true })

    const routerProps = { history, render: this.getRouterPropsRender() }

    return (
      <GenericErrorBoundary>
        <Provider store={this.store}>
          <FlagsProvider features={featureFlags}>
            <Router {...routerProps}>
              <Route
                component={(props) => (
                  <App
                    categories={categories}
                    featureFlags={featureFlags}
                    {...props}
                  />
                )}
                path="/"
                onChange={this.handleRouteChange}
              >
                <Route component={this.renderLandingPage} path="store" />
                <Route component={ContentContainer} path="store/">
                  <Route
                    component={SearchPage}
                    path="search"
                    onLeave={this.clearAllProductFilters}
                  />
                  <Route
                    component={BrandPage}
                    path="b/:slug/:brandId"
                    onLeave={this.clearAllProductFilters}
                  />
                  <Route
                    component={CategoryPage}
                    path=":categorySlug"
                    onLeave={this.clearAllProductFilters}
                  />
                </Route>
                <Route
                  component={ProductDetailsWrapper}
                  path="gp/:slug/:gpId(/:productId)"
                />
              </Route>
            </Router>
          </FlagsProvider>
        </Provider>
      </GenericErrorBoundary>
    )
  }
}

StoreApp.propTypes = {
  /**
   * Will only be populated on category pages.
   */
  categorySlug: PropTypes.string,
  categoryTree: PropTypes.arrayOf(PropTypes.shape(CatalogCategoryShape))
    .isRequired,
  categoryGuides: PropTypes.objectOf(PropTypes.shape(GuidesShape)),
  currentUser: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  showAppPromoBanner: PropTypes.bool.isRequired,
  curatedProductCollections: PropTypes.arrayOf(
    PropTypes.shape(CuratedProductCollectionShape)
  ).isRequired,
  path: PropTypes.string.isRequired,
  featureFlags: PropTypes.object,
  /**
   * @FIXME: This prop is hard to define prop types statically since
   * the keys are based on category slugs in most cases. The primary use case
   * is to hydrate product data cache in redux which is deprecated.
   * @deprecated
   */
  // eslint-disable-next-line react/forbid-prop-types
  collections: PropTypes.object,
  /**
   * This will only be populated on pages that benefit from hydrating hero products.
   * LeafCategoryPage and SearchPage
   */
  hero_products: PropTypes.arrayOf(PropTypes.shape(HeroProductShape)),
  /**
   * Possible this isn't really used anymore. Need to verify.
   */
  searchTerm: PropTypes.string,
  /** QueryClient provided by withReactQueryClientProvider */
  queryClient: PropTypes.object.isRequired,
}

StoreApp.defaultProps = {
  searchTerm: null,
  categorySlug: null,
  categoryGuides: null,
  collections: null,
  currentUser: undefined,
  featureFlags: null,
  hero_products: null,
}

// eslint-disable-next-line import/no-default-export
export default withContextualizedTracking({
  eventLocation: track.EventLocation.STORE,
})(withCurrentUser(withReactQueryClientProvider(StoreApp)))
